import { useCallback, useMemo } from 'react'
import { useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useParams } from 'wouter'
import { useLingui } from '@lingui/react/macro'

import { Suspense } from '@/components/ui/Suspense'
import { PromotionForm } from '@/features/promotion'
import { DialogHeader } from '@/layouts/Dialog'
import { useCloseModal, useHistoryFrom } from '@/Router'
import { Permission, useCheckPermission } from '@/features/auth'

function Promotion({ token }: { token: string }) {
  const { t } = useLingui()
  const closeModal = useCloseModal()
  const readOnly = !useCheckPermission([Permission.PROMOTION_UPDATE])
  const from = useHistoryFrom()

  // When the last open page was the goodie, we return to it when the promotion is closed
  const backOnClose = useMemo(() => {
    if (!from) return false
    return /^\/app\/goodie\/(.+)$/.test(from)
  }, [from])

  const { data } = useSuspenseQuery(AdminAPI.promotion.GET_BY_TOKEN, {
    variables: { token, withGallery: true, withTranslations: true },
    fetchPolicy: 'cache-and-network'
  })

  const promotion = useMemo(
    () =>
      getFragment(
        SharedAPI.promotion.PROMOTION_FRAGMENT,
        data?.getPromotionByToken
      ),
    [data]
  )

  const onClose = useCallback(() => {
    if (backOnClose) window.history.back()
    else closeModal()
  }, [backOnClose, closeModal])

  const onDone = useCallback(
    (_goodies?: GraphQL.PromotionFragmentFragment, deleted?: boolean) => {
      if (deleted) onClose()
    },
    [onClose]
  )

  const promotionName = promotion.name

  return (
    <div className="flex flex-col gap-2 p-5">
      <DialogHeader
        title={promotion?.title}
        description={t({
          comment: 'Description for the promotion Dialog',
          message: `Show/Edit the promotion with the name "${promotionName}"`
        })}
        close={onClose}
      />
      <PromotionForm id={promotion.id} onDone={onDone} readOnly={readOnly} />
    </div>
  )
}

export default function PromotionSuspense() {
  const params = useParams() as { token: string }

  if (!params) return null

  return (
    <Suspense isDialog>
      <Promotion token={params.token} />
    </Suspense>
  )
}
